/**
 * Timeline math: when each element enters, holds, exits, and is cleared off the board.
 * Pure functions of the project; everything the renderer shows is derived from these.
 */

import type { AudioClip, Element, Project, Scene } from "./project.gen";

export const INF = Number.POSITIVE_INFINITY;

export interface ElementTimes {
  /** entrance starts */
  enter0: number;
  /** outline phase ends / fill phase starts (== enter0 when the mode has no outline phase) */
  outline1: number;
  /** entrance complete */
  enter1: number;
  /** exit animation starts (INF = stays) */
  exit0: number;
  /** exit animation complete (INF = stays) */
  exit1: number;
  /** a later scene starts clearing the board (INF = never) */
  clear0: number;
  /** removed by that clearing (INF = never) */
  cleared: number;
  /** how the board is cleared: instantly, wiped or erased */
  clearType: "cut" | "wipe" | "erase" | null;
  clearDir: "left" | "right" | "up" | "down";
  /** last instant the element is visible: min(exit1, cleared) */
  end: number;
}

export type Phase = "before" | "outline" | "fill" | "enter" | "hold" | "exit" | "gone";

export interface PhaseState {
  phase: Phase;
  /** seconds since the phase started */
  local: number;
  /** 0..1 progress through the phase (1 for open-ended phases) */
  u: number;
}

/** Scenes sorted by start time (stable). */
export function sortedScenes(project: Project): Scene[] {
  return project.scenes
    .map((s, i) => [s, i] as const)
    .sort((a, b) => a[0].start - b[0].start || a[1] - b[1])
    .map(([s]) => s);
}

/** Durations of the entrance phases for an element. */
export function enterDurations(el: Element): { outline: number; fill: number } {
  const e = el.enter;
  switch (e.mode) {
    case "draw":
      if (el.type === "image") return { outline: Math.max(0, e.outlineDur), fill: Math.max(0, e.fillDur) };
      if (el.type === "shape" && el.shape?.fill) return { outline: Math.max(0, e.outlineDur), fill: Math.max(0, e.fillDur) };
      return { outline: Math.max(0, e.outlineDur), fill: 0 };
    case "outline":
      return { outline: Math.max(0, e.outlineDur), fill: 0 };
    case "fade":
    case "moveIn":
      return { outline: 0, fill: Math.max(0, e.duration) };
    case "instant":
      return { outline: 0, fill: 0 };
  }
}

/**
 * Absolute times for every element. An element belongs to a scene; its `start` is relative to that
 * scene. It stays on the board until it exits or a later scene clears the board. A scene whose
 * startMode is "clear" removes everything from earlier scenes, except with a "pan" transition, where
 * earlier content stays where it was drawn and the camera travels away from it.
 */
export function elementTimes(project: Project): Map<string, ElementTimes> {
  const scenes = sortedScenes(project);
  const sceneIdx = new Map(scenes.map((s, i) => [s.id, i]));
  // for each scene index, the clearing that removes its content
  type Clear = { t0: number; t1: number; type: ElementTimes["clearType"]; dir: ElementTimes["clearDir"] };
  const none: Clear = { t0: INF, t1: INF, type: null, dir: "right" };
  const clearAt: Clear[] = new Array(scenes.length).fill(none);
  let next = none;
  for (let i = scenes.length - 1; i >= 0; i--) {
    clearAt[i] = next;
    const s = scenes[i];
    if (i > 0 && s.startMode === "clear" && s.transition.type !== "pan") {
      const type = s.transition.type as "cut" | "wipe" | "erase";
      const d = type === "cut" ? 0 : Math.max(0, s.transition.duration);
      next = { t0: s.start, t1: s.start + d, type: d > 0 ? type : "cut", dir: s.transition.direction };
    }
  }

  const out = new Map<string, ElementTimes>();
  for (const el of project.elements) {
    const si = sceneIdx.get(el.sceneId);
    const scene = si === undefined ? undefined : scenes[si];
    const base = scene ? scene.start : 0;
    const enter0 = base + Math.max(0, el.start);
    const d = enterDurations(el);
    const outline1 = enter0 + d.outline;
    const enter1 = outline1 + d.fill;
    let exit0 = INF;
    let exit1 = INF;
    if (el.exit.mode !== "none") {
      exit0 = enter1 + (el.hold === null ? INF : Math.max(0, el.hold));
      exit1 = exit0 + Math.max(0, el.exit.duration);
    }
    const c = si === undefined ? none : clearAt[si];
    out.set(el.id, {
      enter0, outline1, enter1, exit0, exit1,
      clear0: c.t0, cleared: c.t1, clearType: c.type, clearDir: c.dir,
      end: Math.min(exit1, c.t1),
    });  // prettier-ignore
  }
  return out;
}

export function phaseAt(el: Element, tm: ElementTimes, t: number): PhaseState {
  if (t < tm.enter0) return { phase: "before", local: t - tm.enter0, u: 0 };
  if (t >= tm.end) return { phase: "gone", local: t - tm.end, u: 1 };
  if (t >= tm.exit0) {
    const span = tm.exit1 - tm.exit0;
    return { phase: "exit", local: t - tm.exit0, u: span > 0 ? (t - tm.exit0) / span : 1 };
  }
  if (t >= tm.enter1) return { phase: "hold", local: t - tm.enter1, u: 1 };
  const mode = el.enter.mode;
  if (t < tm.outline1) {
    const span = tm.outline1 - tm.enter0;
    return { phase: "outline", local: t - tm.enter0, u: span > 0 ? (t - tm.enter0) / span : 1 };
  }
  const span = tm.enter1 - tm.outline1;
  const u = span > 0 ? (t - tm.outline1) / span : 1;
  return { phase: mode === "draw" ? "fill" : "enter", local: t - tm.outline1, u };
}

/** Index into sortedScenes() of the scene playing at t (the last one that has started). */
export function sceneIndexAt(scenes: Scene[], t: number): number {
  let idx = 0;
  for (let i = 0; i < scenes.length; i++) if (scenes[i].start <= t) idx = i;
  return idx;
}

export function clipDuration(project: Project, clip: AudioClip): number {
  const asset = project.assets.find((a) => a.id === clip.assetId);
  const srcEnd = clip.trimOut ?? asset?.duration ?? 0;
  return Math.max(0, srcEnd - clip.trimIn);
}

/** Length of the video: explicit `duration`, else the end of the last audio clip, entrance or scene start. */
export function projectDuration(project: Project): number {
  if (project.duration !== null && project.duration > 0) return project.duration;
  let end = 0;
  for (const c of project.audio) end = Math.max(end, c.start + clipDuration(project, c));
  const times = elementTimes(project);
  for (const tm of times.values()) {
    end = Math.max(end, tm.enter1 + 1);
    if (Number.isFinite(tm.exit1)) end = Math.max(end, tm.exit1);
  }
  for (const s of project.scenes) end = Math.max(end, s.start + 1);
  return end;
}

/** Frame count for a duration at fps (the last partial frame counts). */
export function frameCount(duration: number, fps: number): number {
  return Math.max(1, Math.ceil(duration * fps - 1e-9));
}
